<template>
  <div v-if="hasContent" v-editable="blok">
    <div
      class="accordion-row group flex cursor-pointer select-none items-start justify-between gap-2 py-8"
      :aria-expanded="isExpanded"
      @click="isExpanded = !isExpanded"
    >
      <span
        v-if="isHeadingLevelNormal"
        class="flex items-center gap-2 leading-tight group-hover:underline"
        :class="{
          uppercase: uppercaseTitle,
        }"
        :style="{ fontWeight: boldTitle ? '700' : '500' }"
      >
        {{ blok.title }}
      </span>
      <Headline
        v-else
        :tag="headingLevel"
        :size="getHeadingSize(headingLevel)"
        class="flex items-center gap-2 leading-tight group-hover:underline"
        :is-uppercase="uppercaseTitle"
        :style="{ fontWeight: boldTitle ? '700' : '500' }"
      >
        {{ blok.title }}
      </Headline>
      <span
        v-if="showArrows"
        class="mt-1 inline-block size-4 shrink-0 transform-gpu transition-transform duration-300 ease-out will-change-transform"
        :class="{ 'rotate-180': isExpanded }"
      >
        <IconDropdown class="block size-full" />
      </span>
    </div>
    <div
      class="accordion-content grid transition-all duration-300 ease-out"
      :class="{ 'grid-rows-[0fr]': !isExpanded, 'grid-rows-[1fr]': isExpanded }"
    >
      <div class="overflow-hidden">
        <div class="cms-text pb-8">
          <div v-html="content" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useRichtext from '~/cms/storyblok/composables/useRichtext'
import type { SbAccordionEntry } from '~/cms/storyblok/types/storyblok.d.ts'
import IconDropdown from '@/assets/icons/ui/dropdown.svg'
import Headline from '../components/ui/headlines/Headline.vue'

type AccordionEntryWithExpanded = Omit<SbAccordionEntry, 'expanded'> & {
  expanded?: boolean
}

const props = withDefaults(
  defineProps<{
    blok: AccordionEntryWithExpanded
    collapsed?: boolean
    headingLevel?: 'normal' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    boldTitle?: boolean
    showArrows?: boolean
    uppercaseTitle?: boolean
  }>(),
  {
    headingLevel: 'normal',
    boldTitle: false,
    showArrows: true,
    uppercaseTitle: true,
  },
)

const { content } = useRichtext(props.blok.body as any)

const hasContent = computed(() => {
  const html = content.value
  if (!html || typeof html !== 'string') return false
  const text = html.replace(/<[^>]*>/g, '').trim()
  return text.length > 0
})

const isExpanded = ref(props.blok.expanded === true)

const isHeadingLevelNormal = ref(props.headingLevel === 'normal')

const getHeadingSize = (level: string) => {
  const sizeMap = {
    h1: '2xl',
    h2: 'xl',
    h3: 'lg',
    h4: 'base',
    h5: 'sm',
    h6: 'xs',
    normal: 'base',
  }
  return sizeMap[level as keyof typeof sizeMap] || 'normal'
}
</script>
